import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '../UI/Modal';
import transferService from '../../api/services/transfer.service';

interface TransferDetailModalProps {
  isOpen: boolean;
  transferId: string | null;
  onClose: () => void;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-start justify-between gap-4 py-1.5 border-b border-gray-50 last:border-0">
    <span className="text-xs text-gray-500 flex-shrink-0">{label}</span>
    <span className="text-xs font-medium text-gray-900 text-right break-all">
      {value}
    </span>
  </div>
);

const statusStyle: Record<string, string> = {
  completed: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
};

const TransferDetailModal: React.FC<TransferDetailModalProps> = ({
  isOpen,
  transferId,
  onClose,
}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['transfer-detail', transferId],
    queryFn: () => transferService.getOne(transferId as string),
    enabled: isOpen && !!transferId,
    staleTime: 60 * 1000,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Transfer Details" size="md">
      {isLoading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500 py-4">
          {error instanceof Error
            ? error.message
            : 'Failed to load transfer'}
        </p>
      )}

      {data && (
        <div className="space-y-4">
          {/* Amount header */}
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-[10px] text-primary-700 uppercase tracking-wide">
                Amount
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {data.currency} {data.amount.toLocaleString()}
              </p>
            </div>
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase ${
                statusStyle[data.status] || 'bg-gray-100 text-gray-700'
              }`}
            >
              {data.status}
            </span>
          </div>

          {/* Sender */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Sender
            </h4>
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <Row label="Name" value={data.senderName} />
              <Row label="Phone" value={data.senderPhone || '—'} />
              <Row label="Telegram ID" value={data.senderTelegramId} />
              <Row
                label="Balance After"
                value={`${data.currency} ${data.senderBalanceAfter.toLocaleString()}`}
              />
            </div>
          </div>

          {/* Receiver */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Receiver
            </h4>
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <Row label="Name" value={data.receiverName} />
              <Row label="Phone" value={data.receiverPhone || '—'} />
              <Row label="Telegram ID" value={data.receiverTelegramId} />
              <Row
                label="Balance After"
                value={`${data.currency} ${data.receiverBalanceAfter.toLocaleString()}`}
              />
            </div>
          </div>

          {/* Meta */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Transfer Info
            </h4>
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <Row label="Transfer ID" value={data.id} />
              {data.note && <Row label="Note" value={data.note} />}
              <Row
                label="Created"
                value={new Date(data.createdAt).toLocaleString()}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default TransferDetailModal;